(function () {
  "use strict";
  if (window.pcoCartControllerInstalled) return;
  window.pcoCartControllerInstalled = true;

  const root = window.Shopify?.routes?.root || "/";
  const ROOT = root.endsWith("/") ? root : root + "/";
  const CART_URL = ROOT + "cart";
  const lang = (document.documentElement.lang || "fr").toLowerCase().split("-")[0];
  const texts = {
    fr: {
      invalid: "Sélectionnez une configuration disponible et une quantité valide avant de l’ajouter au panier.",
      consent: "Veuillez accepter les conditions de fourniture de la licence numérique.",
      rejected: "L’ajout au panier n’a pas pu être terminé. Vérifiez la disponibilité de votre configuration et le contenu du panier avant de réessayer.",
      network: "La confirmation de l’ajout n’a pas pu être reçue. Vérifiez votre panier avant de réessayer pour éviter un doublon.",
      cart: "Vérifier mon panier"
    },
    nl: {
      invalid: "Selecteer een beschikbare configuratie en een geldig aantal voordat u deze aan de winkelwagen toevoegt.",
      consent: "Gelieve de voorwaarden voor de levering van de digitale licentie te aanvaarden.",
      rejected: "Het toevoegen aan de winkelwagen kon niet worden voltooid. Controleer de beschikbaarheid en de inhoud van uw winkelwagen voordat u het opnieuw probeert.",
      network: "De bevestiging van het toevoegen is niet ontvangen. Controleer uw winkelwagen voordat u het opnieuw probeert om dubbele artikelen te voorkomen.",
      cart: "Mijn winkelwagen controleren"
    },
    en: {
      invalid: "Select an available configuration and a valid quantity before adding it to your cart.",
      consent: "Please accept the digital licence delivery conditions.",
      rejected: "The items could not all be added. Check availability and the contents of your cart before trying again.",
      network: "Confirmation could not be received. Check your cart before trying again to avoid duplicate items.",
      cart: "Check my cart"
    }
  };
  const text = texts[lang] || texts.fr;
  let adding = false;
  let errorCount = 0;

  function positiveInteger(value) {
    const raw = String(value == null ? "" : value).trim();
    if (!/^\d+$/.test(raw)) return null;
    const number = Number(raw);
    return Number.isSafeInteger(number) && number > 0 ? number : null;
  }

  function ownedControls(form, selector) {
    return Array.from(form.elements).filter(el =>
      el.matches?.(selector) && !el.disabled && !el.matches(":disabled")
    );
  }

  function clearError(form) {
    form.querySelector("[data-pco-cart-error]")?.remove();
  }

  function showError(form, message, includeCart) {
    clearError(form);
    const box = document.createElement("div");
    box.dataset.pcoCartError = "";
    box.id = "pco-cart-error-" + (++errorCount);
    box.setAttribute("role", "alert");
    box.setAttribute("aria-live", "assertive");
    box.tabIndex = -1;
    box.textContent = message;
    box.style.cssText = "margin:12px 0;padding:12px;border:1px solid #b42318;border-radius:6px;color:#842018;background:#fff8f7;font-size:14px;line-height:1.5";
    if (includeCart) {
      const link = document.createElement("a");
      link.href = CART_URL;
      link.textContent = text.cart;
      link.style.cssText = "display:block;margin-top:8px;text-decoration:underline;color:inherit";
      box.appendChild(link);
    }
    form.appendChild(box);
    box.focus({ preventScroll: true });
    box.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }

  function buildItems(form) {
    const data = new FormData(form);
    const id = positiveInteger(data.get("id"));
    const quantity = positiveInteger(data.has("quantity") ? data.get("quantity") : 1);
    if (!id || !quantity) throw new Error(text.invalid);
    const main = { id, quantity };
    const properties = {};
    for (const [key, value] of data.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && value !== "" && typeof value === "string") properties[match[1]] = value;
    }
    if (Object.keys(properties).length) main.properties = properties;
    const plan = data.get("selling_plan");
    if (plan) {
      main.selling_plan = positiveInteger(plan);
      if (!main.selling_plan) throw new Error(text.invalid);
    }

    const items = [];
    const selectedServices = ownedControls(form, "[data-pcocc-service]:checked");
    for (const input of selectedServices) {
      const serviceId = positiveInteger(input.value);
      if (!serviceId) throw new Error(text.invalid);
      if (!items.some(item => item.id === serviceId)) {
        items.push({ id: serviceId, quantity: 1, properties: { _pcocc_parent_variant_id: String(id) } });
      }
    }

    const selectedOffice = ownedControls(form, 'input[name="pcocc_office"]:checked')[0];
    if (selectedOffice) {
      const officeBlock = selectedOffice.closest(".pcocc-office");
      const consent = officeBlock?.querySelector("[data-pcocc-office-consent]");
      if (!consent || consent.form !== form || consent.disabled || !consent.checked) {
        if (officeBlock) {
          const details = officeBlock.querySelector("details");
          if (details) details.open = true;
          const wrap = officeBlock.querySelector("[data-pcocc-office-consent-wrap]");
          if (wrap) wrap.hidden = false;
        }
        throw new Error(officeBlock?.dataset.pcoConsentError || text.consent);
      }
      const officeId = positiveInteger(selectedOffice.value);
      if (!officeId) throw new Error(text.invalid);
      items.push({
        id: officeId, quantity: 1,
        properties: {
          _pcocc_addon: "Microsoft Office",
          _pcocc_parent_variant_id: String(id),
          _office_digital_consent: "Accepted",
          _office_digital_consent_text: officeBlock.dataset.pcoConsentText || consent.closest("label")?.textContent.trim() || "",
          _office_digital_consent_date: new Date().toISOString()
        }
      });
    }
    items.push(main);
    return items;
  }

  async function add(form) {
    if (adding) return;
    clearError(form);
    let items;
    try { items = buildItems(form); }
    catch (error) { showError(form, error.message || text.invalid, false); return; }
    adding = true;
    const buttons = ownedControls(form, 'button[type="submit"], input[type="submit"], button:not([type])');
    const oldBusy = form.getAttribute("aria-busy");
    buttons.forEach(button => { button.disabled = true; });
    form.setAttribute("aria-busy", "true");
    let confirmed = false;
    try {
      const response = await fetch(ROOT + "cart/add.js", {
        method: "POST", credentials: "same-origin",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ items })
      });
      if (!response.ok) { showError(form, text.rejected, true); return; }
      const result = await response.json();
      if (!result || typeof result !== "object") throw new Error("Unconfirmed cart response");
      confirmed = true;
      window.location.href = CART_URL + "#cmd-ui";
    } catch (error) {
      showError(form, text.network, true);
    } finally {
      if (!confirmed) {
        adding = false;
        buttons.forEach(button => { button.disabled = false; });
        if (oldBusy === null) form.removeAttribute("aria-busy");
        else form.setAttribute("aria-busy", oldBusy);
      }
    }
  }

  document.addEventListener("submit", function (event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    let action;
    try { action = new URL(form.action, window.location.href); } catch (error) { return; }
    if (action.origin !== window.location.origin || !/\/cart\/add(?:\.js)?\/?$/.test(action.pathname)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!adding) add(form);
  }, true);

  document.addEventListener("click", function (event) {
    const button = event.target?.closest?.("button.cmd-btn__sticky, button.cmd-btn--sticky");
    if (!button || !button.closest("modal-maxdesign, #cmd[data-type='modal'], [data-type='modal'], .cmd-modal, .cmd-modal__content")) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    window.location.href = CART_URL + "#cmd-ui";
  }, true);

  let tries = 0;
  const timer = setInterval(function () {
    const modal = window.MaxDesign?.modal?.cart;
    if (++tries > 80 || modal?.__ooCartPatched) { clearInterval(timer); return; }
    if (!modal) return;
    modal.__ooOriginalOpen = modal.open?.bind(modal);
    modal.open = function () {
      try { this.overlay = { state: "close" }; this.state = "close"; this.close?.(); } catch (error) {}
    };
    modal.__ooCartPatched = true;
    clearInterval(timer);
  }, 100);

  // This only controls the optional HTVA row, never tax exemption or checkout amounts.
  function statusVisible(element, rootElement) {
    if (!element || !element.textContent.trim()) return false;
    for (let current = element; current && rootElement.contains(current); current = current.parentElement) {
      const style = window.getComputedStyle(current);
      if (current.hidden || current.getAttribute("aria-hidden") === "true" || style.display === "none" || style.visibility === "hidden") return false;
      if (current === rootElement) break;
    }
    return true;
  }

  function vatValidated() {
    const vat = document.getElementById("oo-tax-exemption");
    if (!vat) return false;
    if (vat.querySelector('[aria-invalid="true"], [aria-busy="true"]')) return false;
    const errors = vat.querySelectorAll("#oo-tax-error, .oo-tax-error, .oo-error, [data-status='error'], [data-status='invalid']");
    if (Array.from(errors).some(el => statusVisible(el, vat))) return false;
    const success = vat.querySelectorAll("#oo-tax-success, .oo-tax-success, .oo-success, [data-status='success']");
    return Array.from(success).some(el => statusVisible(el, vat));
  }

  let frame = null;
  function updateVatRow() {
    frame = null;
    const line = document.getElementById("cmd-htva-line");
    if (!line) return;
    const display = vatValidated() ? "" : "none";
    if (line.style.display !== display) line.style.display = display;
  }
  function scheduleVatRow() {
    if (frame === null) frame = window.requestAnimationFrame(updateVatRow);
  }
  document.addEventListener("DOMContentLoaded", scheduleVatRow);
  window.addEventListener("load", scheduleVatRow);
  new MutationObserver(scheduleVatRow).observe(document.documentElement, {
    childList: true, subtree: true, characterData: true, attributes: true,
    attributeFilter: ["class", "style", "hidden", "aria-hidden", "aria-invalid", "aria-busy", "data-status"]
  });
  scheduleVatRow();

  const style = document.createElement("style");
  style.textContent = "modal-maxdesign button.cmd-btn__sticky span[hidden],modal-maxdesign button.cmd-btn--sticky span[hidden]{display:inline!important;visibility:visible!important}";
  document.head.appendChild(style);
})();
